import { motion } from "framer-motion";
import { Newspaper, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n";

const articles = [
  {
    source: "La Pulce nell'Orecchio",
    date: "3 Febbraio 2026",
    title: "Yurj Selvaggio e la sua nuova vita in Albania: «In Italia solo problemi, qui sicurezza e agevolazioni»",
    excerpt: "Il ritratto dell'imprenditore alessandrino che ha trovato a Durazzo la serenità professionale: un'agenzia per il lavoro sanitario, investimenti e una nuova vita sull'Adriatico.",
    img: "/images/press-albania.jpg",
    link: "https://lapulceonline.it/2026/02/03/yurj-selvaggio-e-la-sua-nuova-vita-in-albania-in-italia-solo-problemi-qui-sicurezza-e-agevolazioni/personaggi/",
  },
  {
    source: "Il Piccolo — AlessandriaNews",
    date: "26 Marzo 2024",
    title: "Brics Gold, la moneta virtuale per il mondo del gioiello e del lusso",
    excerpt: "Dal welfare alle criptovalute: l'idea di Yuri Selvaggio per far commerciare le imprese del distretto orafo di Valenza con i Paesi BRICS, in sicurezza e senza intermediari.",
    img: "/images/press-bricsgold.jpg",
    link: "https://alessandrianews.ilpiccolo.net/2024/03/26/brics-gold-la-moneta-virtuale-per-il-mondo-del-gioiello-e-del-lusso/",
  },
  {
    source: "Radio Gold",
    date: "31 Agosto 2020",
    title: "Un microbiglietto da visita sempre «a portata di mano»: parla un alessandrino col chip sottopelle",
    excerpt: "Pioniere della tecnologia: Yuri racconta la scelta del microchip NFC/RFID impiantato nella mano, tra credenziali digitali, badge e uno sguardo al futuro dei pagamenti.",
    img: "/images/press-microchip.jpg",
    link: "https://radiogold.it/news-alessandria/cronaca/241072-microchip-sottocutaneo-2020-italia-news/",
  },
  {
    source: "Il Piccolo",
    date: "21 Novembre 2023",
    title: "Telemedicina e servizi sanitari a distanza: TEO Salute",
    excerpt: "Una pagina dedicata al bracciale TEO e alla visione di Yurax Project: parametri vitali monitorati in tempo reale, servizi infermieristici e formazione professionale.",
    img: "/images/press-teo-salute.webp",
  },
  {
    source: "Stampa locale",
    date: "2023",
    title: "Badante di condominio: l'assistenza condivisa arriva ad Alessandria",
    excerpt: "Il servizio ideato da YUMA Coop finisce sui giornali: assistenza tutelata e costi condivisi tra i condomini per le famiglie che assistono anziani e disabili.",
    img: "/images/press-badante.jpg",
  },
  {
    source: "Stampa economica — Spagna",
    date: "2023",
    title: "Notarchain.es, la plataforma para certificar contratos inteligentes",
    excerpt: "La stampa economica spagnola racconta la piattaforma di Yuri Selvaggio per certificare atti e contratti su blockchain: «Estamos en un momento de revolución económica mundial».",
    img: "/images/press-notarchain.jpg",
  },
];

export const Stampa = () => {
  const { t } = useLang();
  return (
    <section id="stampa" className="relative py-24 sm:py-32 bg-gradient-to-b from-[#ECFEFF] via-[#F7FBFD] to-white overflow-hidden" data-testid="stampa-section">
      <img src="/images/logo-watermark.png" alt="" aria-hidden="true"
        className="pointer-events-none select-none absolute -left-28 -bottom-16 w-[32rem] max-w-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <span className="inline-flex items-center gap-2 font-mono-ed text-xs uppercase tracking-[0.22em] text-[#0284C7]">
            <Newspaper size={14} /> {t.stampa.label}
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0B2530] mt-4 leading-tight">
            {t.stampa.h2a}<em className="font-serif-ed italic font-medium text-[#06B6D4]">{t.stampa.h2em}</em>{t.stampa.h2b}
          </h2>
          <p className="text-[#3D5A66] mt-5 text-base sm:text-lg">
            {t.stampa.intro}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mt-14">
          {articles.map((a, i) => (
            <motion.a
              key={a.title}
              href={a.link || undefined}
              target={a.link ? "_blank" : undefined}
              rel={a.link ? "noopener noreferrer" : undefined}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className={`group rounded-3xl bg-white border border-[#DCEEF5] overflow-hidden hover:-translate-y-1.5 hover:shadow-xl hover:shadow-sky-500/10 transition-all duration-300 flex flex-col ${a.link ? "" : "cursor-default"}`}
              data-testid={`stampa-card-${i}`}
            >
              <div className="relative h-52 overflow-hidden">
                <img src={a.img} alt={a.title} loading="lazy"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                <span className="absolute top-4 left-4 rounded-full bg-white/90 backdrop-blur px-3.5 py-1.5 text-[11px] font-mono-ed uppercase tracking-[0.14em] text-[#0369A1]">
                  {a.source}
                </span>
              </div>
              <div className="p-7 flex flex-col flex-1">
                <span className="text-xs text-[#5F7E8A] font-medium">{a.date}</span>
                <h3 className="font-display text-lg font-bold text-[#0B2530] mt-2 leading-snug group-hover:text-[#0284C7] transition-colors duration-300">
                  {a.title}
                </h3>
                <p className="text-sm text-[#3D5A66] leading-relaxed mt-3 flex-1">{a.excerpt}</p>
                {a.link ? (
                  <span className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-[#0284C7]">
                    {t.stampa.read}
                    <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                ) : (
                  <span className="mt-5 text-xs font-mono-ed uppercase tracking-[0.16em] text-[#5F7E8A]">{t.stampa.print}</span>
                )}
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};
